<script setup lang="ts">
import SvgIcon from "~/components/Common/SvgIcon.vue";
import Tooltip from "~/components/Common/Tooltip.vue";
import CheckboxInput from "~/components/Common/Inputs/CheckboxInput.vue";
import CommonInput from "~/components/Common/Inputs/CommonInput.vue";
import RadioInput from "~/components/Common/Inputs/RadioInput.vue";
import { useAddSessionFlow } from "~/composables/trainings/useAddSessionFlow";
import { SessionItemModalityEnum, SessionItemTypeEnum } from "~/utils/enums/session-items.enums";
import { TrainingTypeEnum } from "~/utils/enums/training.enums";
import { followsDoceboRules, overbookingNotAllowedFor } from "~/utils/helpers/sessions/session-capacity";

const flow = useAddSessionFlow();

/** A self-paced individual item cannot host a dated session, so its card stays out of reach. */
const isIndividualDisabled = computed(
  () => !flow.training.individual.isChecked || flow.training.individual.modality === SessionItemModalityEnum.ON_DEMAND,
);

const followsDocebo = computed(() => followsDoceboRules(flow.lmsProvider));

/** Capacity and format are inherited from the session item the admin just picked. */
function applySessionItemDefaults() {
  const draft = flow.sessions.sessionDraft;
  if (!draft?.sessionItemType) return;

  const sessionItem =
    draft.sessionItemType === SessionItemTypeEnum.INDIVIDUAL ? flow.training.individual : flow.training.group;
  if (!sessionItem) return;

  draft.format = flow.training.format;
  draft.minNbAttendees = sessionItem.minNbAttendees;
  draft.maxNbAttendees = sessionItem.maxNbAttendees;
  draft.overbookingNotAllowed = overbookingNotAllowedFor(
    sessionItem.overbookingNotAllowed,
    sessionItem.maxNbAttendees,
    followsDocebo.value,
  );
}

watch(() => flow.sessions.sessionDraft?.sessionItemType, applySessionItemDefaults);

watch([() => flow.sessions.sessionDraft?.maxNbAttendees, followsDocebo], ([max]) => {
  const draft = flow.sessions.sessionDraft;
  if (!draft) return;
  draft.overbookingNotAllowed = overbookingNotAllowedFor(draft.overbookingNotAllowed, max, followsDocebo.value);
});

onMounted(applySessionItemDefaults);
</script>

<template>
  <div v-if="flow.sessions.sessionDraft" class="flex flex-col">
    <h4 class="heading_4_demi">Détails de la session</h4>

    <div class="mt-6 flex flex-col gap-6">
      <div class="flex flex-col gap-2">
        <span class="text-secondary-color">Quel type d'inscription ?</span>
        <div class="flex gap-2">
          <label
            class="session_item_label__container flex flex-1 items-center justify-between rounded-lg border border-stroke-color bg-light-color p-[14px]"
          >
            <span class="grid">
              <div v-if="flow.training.type === TrainingTypeEnum.EXTERNAL">
                <SvgIcon
                  class="w-6 h-6"
                  :class="isIndividualDisabled ? 'text-medium-grey-color' : 'text-primary-color'"
                  name="indiv"
                />
                <span class="body_med">Inscription individuelle</span>
              </div>
              <div v-else class="flex flex-col">
                <SvgIcon
                  class="w-6 h-6"
                  :class="isIndividualDisabled ? 'text-medium-grey-color' : 'text-primary-color'"
                  name="graduation_cap_on_v2"
                />
                <span class="body_med">Inscription interne</span>
              </div>
              <span v-if="isIndividualDisabled">(Indisponible pour cette formation)</span>
            </span>
            <RadioInput
              v-model="flow.sessions.sessionDraft.sessionItemType"
              type="radio"
              :disabled="isIndividualDisabled"
              :value="SessionItemTypeEnum.INDIVIDUAL"
            />
          </label>

          <label
            v-if="flow.training.group"
            class="session_item_label__container flex flex-1 items-center justify-between rounded-lg border border-stroke-color bg-light-color p-[14px]"
          >
            <span class="grid">
              <SvgIcon
                class="w-6 h-6"
                :class="!flow.training.group.isChecked ? 'text-medium-grey-color' : 'text-primary-color'"
                name="group"
              />
              <span class="body_med">Session collective</span>
              <span v-if="!flow.training.group.isChecked">(Indisponible pour cette formation)</span>
            </span>
            <RadioInput
              v-model="flow.sessions.sessionDraft.sessionItemType"
              type="radio"
              :disabled="!flow.training.group.isChecked"
              :value="SessionItemTypeEnum.GROUP"
            />
          </label>
        </div>
        <span v-if="flow.errors['sessions.sessionDraft.sessionItemType']" class="text-primary-red-color body_med">
          {{ flow.errors["sessions.sessionDraft.sessionItemType"] }}
        </span>
      </div>

      <div class="flex flex-col gap-3">
        <div class="flex gap-1 items-center">
          <span class="text-secondary-color">Nombre de places</span>
          <Tooltip :position="'top-right'" :svg-width="16">
            <template #content>
              <div class="flex w-[414px] gap-[8px]">
                <img src="/icons/emojis/eyes.svg" alt="" class="h-4 w-4" />
                <span class="font-medium">Si la capacité max est vide alors il n’y a pas de limite d’inscription.</span>
              </div>
            </template>
          </Tooltip>
        </div>

        <div class="flex gap-4">
          <div class="flex gap-2 items-center">
            <div class="w-[88px]">
              <CommonInput
                v-model="flow.sessions.sessionDraft.minNbAttendees"
                :nullable="true"
                :min="0"
                type="number"
                placeholder="-"
              />
            </div>
            <span class="text-secondary-color">min</span>
          </div>
          <div class="flex gap-2 items-center">
            <div class="w-[88px]">
              <CommonInput
                v-model="flow.sessions.sessionDraft.maxNbAttendees"
                :nullable="true"
                :min="0"
                :error="!!flow.errors['sessions.sessionDraft.maxNbAttendees']"
                type="number"
                placeholder="-"
              />
            </div>
            <span class="text-secondary-color">max</span>
          </div>
        </div>
        <span v-if="flow.errors['sessions.sessionDraft.maxNbAttendees']" class="text-primary-red-color body_med">
          {{ flow.errors["sessions.sessionDraft.maxNbAttendees"] }}
        </span>

        <div v-if="!followsDocebo" class="flex items-center gap-2">
          <label
            class="flex items-center gap-2"
            :class="
              Number.isFinite(flow.sessions.sessionDraft.maxNbAttendees) ? 'cursor-pointer' : 'cursor-not-allowed'
            "
          >
            <CheckboxInput
              v-if="Number.isFinite(flow.sessions.sessionDraft.maxNbAttendees)"
              v-model:model-value="flow.sessions.sessionDraft.overbookingNotAllowed"
              :checked="flow.sessions.sessionDraft.overbookingNotAllowed"
              @update:model-value="
                (value) => {
                  if (flow.sessions.sessionDraft) flow.sessions.sessionDraft.overbookingNotAllowed = value;
                }
              "
            />
            <Tooltip v-else :position="'top-right'">
              <template #img>
                <CheckboxInput
                  v-model:model-value="flow.sessions.sessionDraft.overbookingNotAllowed"
                  :disabled="!Number.isFinite(flow.sessions.sessionDraft.maxNbAttendees)"
                  :checked="flow.sessions.sessionDraft.overbookingNotAllowed"
                  @update:model-value="
                    (value) => {
                      if (flow.sessions.sessionDraft) flow.sessions.sessionDraft.overbookingNotAllowed = value;
                    }
                  "
                />
              </template>
              <template #content>
                <div class="flex w-[414px] gap-[8px]">
                  <img src="/icons/emojis/eyes.svg" alt="" class="h-4 w-4" />
                  <span class="font-medium"
                    >Pour bloquer les inscriptions vous devez saisir un nombre de place maximal.</span
                  >
                </div>
              </template>
            </Tooltip>
            <span class="text-secondary-color">Bloquer les inscriptions une fois la capacité max atteinte</span>
          </label>
          <Tooltip :position="'top-right'">
            <template #content>
              <div class="flex w-[414px] gap-[8px]">
                <img src="/icons/emojis/eyes.svg" alt="" class="h-4 w-4" />
                <span class="font-medium"
                  >Le nombre de places est modifiable sur chaque session. Si vous cochez cette case, une fois le nombre
                  de place max atteint les participants ne pourront plus s’inscrire mais ils pourront être ajoutés par
                  un Admin ou un Référent.</span
                >
              </div>
            </template>
          </Tooltip>
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <span class="text-secondary-color">Indication</span>
        <CommonInput
          v-model="flow.sessions.sessionDraft.designation"
          style="text-align: start"
          placeholder="A définir"
          type="text"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
// Only the `:has()` rules stay in CSS: "the label whose radio is checked" has no Tailwind variant.
// Everything static lives on the element itself.
.session_item_label__container {
  &:has(input[type="radio"]:not([disabled])) {
    @apply cursor-pointer;

    &:hover,
    &:has(input:checked) {
      @apply border-primary-blue-color;
    }
  }

  &:has(input[type="radio"]:disabled) {
    @apply text-medium-grey-color;
  }
}
</style>
